import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flag } from 'lucide-react';
import { profilesAPI } from '../../services/api';

// 紧急客户 — manual urgent flag (client_urgent_flags).
//
//  default UrgentFlagButton  客户记录 header: 「标记紧急」 (red outline) → small popover with
//                            原因（必填） / 希望解决的日期（可选）; when flagged 「解除紧急」.
//  UrgentFlagTag             客户记录 header, beside the name: red 「紧急」 tag (reason on hover).
//  UrgentTag                 pure tag for list rows (row.urgent_flag from GET /profiles).
//  UrgentFlagDialog          the same form as a centred dialog (档案列表 row ⋯ menu).
//  clearUrgentFlagWithConfirm(profileId, isZh) → Promise<boolean>.
//
// Before the backend ships, GET /profiles/:id/urgent-flag 404s: the button and the tag
// render nothing. Set / clear broadcast a window event so the header tag and button (two
// separate components) stay in step without another fetch round.

const C = {
  danger: '#9E2B25', ink: '#111111', ink2: '#3A3835', muted: '#6E685F', raised: '#FFFDF9',
  line: 'rgba(17,17,17,.2)',
};
const EVT = 'findu:urgent-flag-changed';

// One GET per profile at a time (the tag and the button both ask on open).
const inflight = new Map();
function fetchFlag(profileId) {
  const key = String(profileId);
  if (inflight.has(key)) return inflight.get(key);
  const p = profilesAPI.getUrgentFlag(profileId)
    .then(r => {
      const d = r && r.data;
      // A real answer always carries can_edit; anything else (HTML fallback, old backend) = unavailable.
      if (!d || typeof d !== 'object' || !('can_edit' in d)) return null;
      return { flag: d.flag || null, can_edit: !!d.can_edit };
    })
    .catch(() => null)
    .finally(() => { setTimeout(() => inflight.delete(key), 1500); });
  inflight.set(key, p);
  return p;
}
const announce = (profileId, flag) => {
  inflight.delete(String(profileId));
  try { window.dispatchEvent(new CustomEvent(EVT, { detail: { profileId: Number(profileId), flag: flag || null } })); } catch (e) { /* ignore */ }
};

function useUrgentFlag(profileId) {
  const [state, setState] = useState(null); // null = unknown / unavailable
  useEffect(() => {
    let alive = true;
    if (!profileId) { setState(null); return undefined; }
    fetchFlag(profileId).then(s => { if (alive) setState(s); });
    const on = (e) => {
      if (!e.detail || Number(e.detail.profileId) !== Number(profileId)) return;
      setState(s => (s ? { ...s, flag: e.detail.flag } : s));
    };
    window.addEventListener(EVT, on);
    return () => { alive = false; window.removeEventListener(EVT, on); };
  }, [profileId]);
  return state;
}

const mdOf = (ymd) => (ymd ? `${Number(String(ymd).slice(5, 7))}/${Number(String(ymd).slice(8, 10))}` : '');
export const urgentTitle = (flag, isZh) => {
  if (!flag) return '';
  const t = (zh, en) => (isZh ? zh : en);
  return [
    `${t('紧急', 'Urgent')} · ${flag.reason || ''}`,
    flag.due_date ? t(`希望 ${mdOf(flag.due_date)} 前解决`, `Resolve by ${mdOf(flag.due_date)}`) : '',
    flag.set_by_name ? t(`标记人：${flag.set_by_name}`, `Flagged by ${flag.set_by_name}`) : '',
  ].filter(Boolean).join('\n');
};

export function UrgentTag({ flag, isZh = true, size = 'md' }) {
  if (!flag) return null;
  const sm = size === 'sm';
  return (
    <span title={urgentTitle(flag, isZh)}
      style={{ display: 'inline-flex', alignItems: 'center', fontSize: sm ? 11 : 12, lineHeight: 1.3, padding: sm ? '1px 6px' : '3px 8px',
        background: C.danger, color: C.raised, fontWeight: 400, letterSpacing: '.08em', whiteSpace: 'nowrap', cursor: 'help', flex: '0 0 auto' }}>
      {isZh ? '紧急' : 'Urgent'}
    </span>
  );
}

export function UrgentFlagTag({ profileId, isZh = true }) {
  const s = useUrgentFlag(profileId);
  return s && s.flag ? <UrgentTag flag={s.flag} isZh={isZh} /> : null;
}

export async function clearUrgentFlagWithConfirm(profileId, isZh = true) {
  if (!window.confirm(isZh ? '解除这个会员的紧急标记？' : 'Clear the urgent flag for this member?')) return false;
  try {
    await profilesAPI.clearUrgentFlag(profileId);
    announce(profileId, null);
    return true;
  } catch (e) {
    window.alert((e && e.response && e.response.data && e.response.data.error) || (isZh ? '解除失败，请稍后再试' : 'Could not clear the flag'));
    return false;
  }
}

const field = {
  height: 44, boxSizing: 'border-box', border: `1px solid ${C.line}`, background: C.raised, padding: '0 14px',
  font: 'inherit', fontSize: 15, color: C.ink, width: '100%', borderRadius: 0, outline: 'none',
};
const btn = {
  height: 40, padding: '0 14px', border: `1px solid ${C.line}`, background: C.raised, font: 'inherit', fontSize: 14,
  color: C.ink, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderRadius: 0, fontWeight: 400,
};

// The form itself (title, 原因, 日期, 取消 / 确认标记). Escape cancels without closing whatever
// is underneath (the 客户记录 modal closes on Escape too).
function UrgentFlagForm({ profileId, isZh, onCancel, onSaved, style }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [reason, setReason] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      onCancel && onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const submit = async () => {
    const r = reason.trim();
    if (!r) { setErr(t('请填写原因', 'Please give a reason')); return; }
    setBusy(true); setErr('');
    try {
      const res = await profilesAPI.setUrgentFlag(profileId, { reason: r, due_date: due || null });
      const flag = (res && res.data && res.data.flag) || { reason: r, due_date: due || null };
      announce(profileId, flag);
      onSaved && onSaved(flag);
    } catch (e) {
      const st = e && e.response && e.response.status;
      setErr((e && e.response && e.response.data && e.response.data.error)
        || (st === 404 ? t('后端未部署', 'Not available yet') : t('保存失败，请稍后再试', 'Could not save')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-label={t('标记为紧急客户', 'Mark as urgent')} onClick={(e) => e.stopPropagation()}
      style={{ width: 440, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', background: C.raised, border: `1px solid ${C.ink}`,
        boxShadow: '0 14px 32px rgba(17,17,17,.12)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
        color: C.ink, textAlign: 'left', fontFamily: 'inherit', ...style }}>
      <span style={{ fontSize: 16 }}>{t('标记为紧急客户', 'Mark as urgent client')}</span>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 13, color: C.ink2, letterSpacing: '.06em' }}>{t('原因（必填）', 'Reason (required)')}</span>
        <input ref={inputRef} value={reason} maxLength={500} onChange={(e) => { setReason(e.target.value); if (err) setErr(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) submit(); }}
          placeholder={t('例如：下周回国，希望这周内见到第 2 位推荐', 'e.g. back home next week, wants the 2nd intro this week')} style={field} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 13, color: C.ink2, letterSpacing: '.06em' }}>{t('希望解决的日期（可选）', 'Resolve by (optional)')}</span>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={field} />
      </label>
      {err && <span style={{ fontSize: 13, color: C.danger }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" onClick={onCancel} disabled={busy} style={btn}>{t('取消', 'Cancel')}</button>
        <button type="button" onClick={submit} disabled={busy}
          style={{ ...btn, background: C.danger, borderColor: C.danger, color: C.raised, opacity: busy ? 0.6 : 1 }}>
          {busy ? t('保存中…', 'Saving…') : t('确认标记', 'Mark urgent')}
        </button>
      </div>
    </div>
  );
}

// Centred dialog — used from the 档案列表 row menu.
export function UrgentFlagDialog({ profileId, profileName, isZh = true, onClose, onSaved }) {
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 60000, background: 'rgba(17,17,17,.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {profileName ? <span style={{ fontSize: 13, color: C.raised, letterSpacing: '.06em' }}>{profileName}</span> : null}
        <UrgentFlagForm profileId={profileId} isZh={isZh} onCancel={onClose} onSaved={(f) => { onSaved && onSaved(f); onClose && onClose(); }} />
      </div>
    </div>
  );
}

// 客户记录 header button.
export default function UrgentFlagButton({ profileId, isZh = true, compact = false, onChanged }) {
  const s = useUrgentFlag(profileId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const t = (zh, en) => (isZh ? zh : en);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!s || !s.can_edit) return null;

  const outline = { ...btn, borderColor: C.danger, color: C.danger, flex: '0 0 auto', whiteSpace: 'nowrap', padding: compact ? '0 10px' : '0 14px' };

  if (s.flag) {
    return (
      <button type="button" disabled={busy} title={urgentTitle(s.flag, isZh)}
        onClick={async () => {
          setBusy(true);
          const ok = await clearUrgentFlagWithConfirm(profileId, isZh);
          setBusy(false);
          if (ok && onChanged) onChanged(null);
        }}
        style={{ ...outline, opacity: busy ? 0.6 : 1 }}>
        <Flag size={16} strokeWidth={1.4} />{compact ? '' : t('解除紧急', 'Clear urgent')}
      </button>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} title={t('标记为紧急客户', 'Mark as urgent client')} style={outline}>
        <Flag size={16} strokeWidth={1.4} />{compact ? '' : t('标记紧急', 'Mark urgent')}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 40 }}>
          <UrgentFlagForm profileId={profileId} isZh={isZh} onCancel={close}
            onSaved={(f) => { setOpen(false); if (onChanged) onChanged(f); }} />
        </div>
      )}
    </div>
  );
}
